import type {
  ConnectedRepository,
  RecentAnalysis,
  DashboardStats,
  UserActivityMetrics,
  GuidancePreferences,
  GuidanceInteractions,
} from '../types/dashboard';

import {
  DEFAULT_GUIDANCE_PREFERENCES,
  DEFAULT_GUIDANCE_INTERACTIONS,
  DEFAULT_USER_ACTIVITY_METRICS,
} from '../types/dashboard';

import type { DashboardData } from './DashboardService';

export interface RawAnalyzedRepository {
  repositoryId: string;
  repositoryName: string;
  repositoryType?: string;
  repositoryUrl?: string;
  status?: string;
  hasAnalysis?: boolean;
  analysisScore?: number | null;
  maintenanceScore?: number | null;
  analysisCount?: number;
  lastAccessedAt?: string;
}

export interface RawConnectedRepository {
  id: string;
  name: string;
  fullName?: string;
  owner?: string;
  provider: 'github' | 'gitlab' | 'bitbucket' | 'upload';
  status?: string;
  lastAnalysis?: string;
  analysisCount?: number;
  devScore?: number | null;
  healthScore?: number | null;
  maintenanceScore?: number | null;
  url: string;
}

export interface RawAnalysis {
  id: string;
  repositoryId: string;
  repositoryName: string;
  startedAt: string;
  completedAt?: string | null;
  status: string;
  overallScore?: number | null;
  issuesFound?: number | null;
}

export interface RawDashboardData {
  user: {
    id: string;
    name: string;
    email: string;
    joinedAt: string;
    isFirstVisit?: boolean;
    guidanceCompleted?: boolean;
    lastActiveDate?: string;
    activityMetrics?: UserActivityMetrics;
  };
  guidance?: {
    enabled?: boolean;
    currentStep?: number;
    completedSteps?: number[];
    dismissed?: boolean;
    lastShown?: string;
    completionRate?: number;
    userPreferences?: GuidancePreferences;
    interactions?: GuidanceInteractions;
  };
  repositories?: RawConnectedRepository[];
  recentAnalyses?: RawAnalysis[];
  stats?: DashboardStats;
}

export function transformAnalyzedRepository(repo: RawAnalyzedRepository): ConnectedRepository {
  let provider: 'github' | 'gitlab' | 'bitbucket' | 'upload' = 'github';
  let platform: 'github' | 'gitlab' | 'bitbucket' | 'upload' = 'github';
  let source: 'git' | 'upload' | 'oauth' = 'oauth';

  if (repo.repositoryType) {
    const type = repo.repositoryType.toLowerCase();
    if (type === 'gitlab') {
      provider = 'gitlab';
      platform = 'gitlab';
      source = 'oauth';
    } else if (type === 'bitbucket') {
      provider = 'bitbucket';
      platform = 'bitbucket';
      source = 'oauth';
    } else if (type === 'upload' || type === 'sample') {
      provider = 'upload';
      platform = 'upload';
      source = 'upload';
    } else {
      provider = 'github';
      platform = 'github';
      source = 'oauth';
    }
  }

  let status: ConnectedRepository['status'];
  const apiStatus = repo.status?.toLowerCase();

  if (apiStatus === 'analyzing' || apiStatus === 'processing') {
    status = 'analyzing';
  } else if (apiStatus === 'error' || apiStatus === 'failed') {
    status = 'error';
  } else if (apiStatus === 'completed') {
    if (repo.hasAnalysis && repo.analysisScore != null) {
      if (repo.analysisScore < 60) {
        status = 'issues';
      } else {
        status = 'healthy';
      }
    } else {
      status = 'healthy';
    }
  } else {
    status = 'healthy';
  }

  return {
    id: repo.repositoryId,
    name: repo.repositoryName,
    fullName: repo.repositoryName,
    provider,
    platform,
    source,
    status,
    lastAnalysis: repo.lastAccessedAt ? new Date(repo.lastAccessedAt) : null,
    analysisCount: repo.analysisCount ?? (repo.hasAnalysis ? 1 : 0),
    devScore: repo.analysisScore ?? null,
    maintenanceScore: repo.maintenanceScore ?? null,
    url: repo.repositoryUrl || '',
    cloneUrl: repo.repositoryUrl || '',
  };
}

export function transformRepositoryToAnalysis(repo: RawAnalyzedRepository): RecentAnalysis {
  const lastAccessedAt = repo.lastAccessedAt ? new Date(repo.lastAccessedAt) : new Date();
  const analysisScore = repo.analysisScore;
  const status = repo.status || 'pending';
  const hasAnalysis = repo.hasAnalysis;

  let summary = '';
  if (status === 'completed' && hasAnalysis && analysisScore != null) {
    if (analysisScore >= 90) {
      summary = `Excellent! Score: ${analysisScore}/100`;
    } else if (analysisScore >= 75) {
      summary = `Good quality with room for improvement`;
    } else if (analysisScore >= 60) {
      summary = `Moderate quality, some issues found`;
    } else {
      summary = `Needs attention to improve quality`;
    }
  } else if (status === 'completed' && !hasAnalysis) {
    summary = 'Analysis completed, awaiting results';
  } else if (status === 'analyzing') {
    summary = 'Analysis in progress...';
  } else if (status === 'failed') {
    summary = 'Analysis failed';
  } else {
    summary = 'Pending analysis';
  }

  return {
    id: repo.repositoryId,
    repositoryId: repo.repositoryId,
    repositoryName: repo.repositoryName,
    startedAt: lastAccessedAt,
    completedAt: lastAccessedAt,
    status: status as RecentAnalysis['status'],
    overallScore: analysisScore ?? null,
    issuesFound: 0,
    summary: summary,
    detailUrl: `/repository/${repo.repositoryId}`,
  };
}

export function mapRepositoryStatus(status: string | undefined): ConnectedRepository['status'] {
  switch (status?.toLowerCase()) {
    case 'connected':
    case 'active':
      return 'healthy';
    case 'analyzing':
    case 'running':
      return 'analyzing';
    case 'issues':
    case 'warnings':
      return 'issues';
    case 'error':
    case 'failed':
      return 'error';
    default:
      return 'healthy';
  }
}

export function generateNonTechnicalSummary(analysis: RawAnalysis): string {
  if (!analysis.completedAt) {
    return 'Analysis in progress...';
  }

  if (analysis.status === 'failed') {
    return 'Analysis could not be completed';
  }

  const score = analysis.overallScore || 0;
  const issues = analysis.issuesFound || 0;

  if (score >= 90) {
    return `Excellent code quality! ${issues === 0 ? 'No issues found.' : `${issues} minor suggestions.`}`;
  } else if (score >= 75) {
    return `Good code quality with ${issues} areas for improvement.`;
  } else if (score >= 60) {
    return `Code quality is okay, found ${issues} issues to address.`;
  } else {
    return `Found ${issues} issues that need attention to improve code quality.`;
  }
}

export function transformRepository(repo: RawConnectedRepository): ConnectedRepository {
  return {
    id: repo.id,
    name: repo.name,
    fullName: repo.fullName || `${repo.owner}/${repo.name}`,
    provider: repo.provider,
    status: mapRepositoryStatus(repo.status),
    lastAnalysis: repo.lastAnalysis ? new Date(repo.lastAnalysis) : null,
    analysisCount: repo.analysisCount || 0,
    devScore: repo.devScore ?? repo.healthScore ?? null,
    maintenanceScore: repo.maintenanceScore ?? null,
    url: repo.url,
  };
}

export function transformAnalysis(analysis: RawAnalysis): RecentAnalysis {
  return {
    id: analysis.id,
    repositoryId: analysis.repositoryId,
    repositoryName: analysis.repositoryName,
    startedAt: new Date(analysis.startedAt),
    completedAt: analysis.completedAt ? new Date(analysis.completedAt) : null,
    status: analysis.status as RecentAnalysis['status'],
    overallScore: analysis.overallScore ?? null,
    issuesFound: analysis.issuesFound ?? null,
    summary: generateNonTechnicalSummary(analysis),
    detailUrl: `/jobs/${analysis.id}/results`,
  };
}

export function transformDashboardData(data: RawDashboardData): DashboardData {
  return {
    user: {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      joinedAt: new Date(data.user.joinedAt),
      isFirstVisit: data.user.isFirstVisit || false,
      guidanceCompleted: data.user.guidanceCompleted || false,
      lastActiveDate: data.user.lastActiveDate ? new Date(data.user.lastActiveDate) : new Date(),
      activityMetrics: data.user.activityMetrics || DEFAULT_USER_ACTIVITY_METRICS,
    },
    guidance: {
      enabled: data.guidance?.enabled || false,
      currentStep: data.guidance?.currentStep || 0,
      completedSteps: data.guidance?.completedSteps || [],
      dismissed: data.guidance?.dismissed || false,
      lastShown: data.guidance?.lastShown ? new Date(data.guidance.lastShown) : new Date(),
      completionRate: data.guidance?.completionRate || 0,
      userPreferences: data.guidance?.userPreferences || DEFAULT_GUIDANCE_PREFERENCES,
      interactions: data.guidance?.interactions || DEFAULT_GUIDANCE_INTERACTIONS,
    },
    repositories:
      data.repositories?.map((repo: RawConnectedRepository) => transformRepository(repo)) || [],
    recentAnalyses:
      data.recentAnalyses?.map((analysis: RawAnalysis) => transformAnalysis(analysis)) || [],
    stats: data.stats || {
      totalRepositories: 0,
      totalAnalyses: 0,
      averageScore: 0,
      issuesResolved: 0,
    },
  };
}
